import React, { useState, useEffect } from 'react';
import { useMutation } from 'react-query';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  TextField,
  Button,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Backdrop
} from '@mui/material';
import { toast } from 'sonner';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import client from '../../../../API';
import CampaignDialog from '../campaigndialog';
import PartnershipDialog from './editingcollabs/partnershipdialog';
import DeliverableDialog from './editingcollabs/deliverabledialog';
import { StyleButton } from '../../../../Utils/ThemedStyles';

const UnifiedCollaborationAdminView = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [collaborationData, setCollaborationData] = useState({
    campaigns: [],
    partnerships: [],
    deliverables: [],
    summary: {}
  });
  const [selectedItems, setSelectedItems] = useState({
    campaigns: {},
    partnerships: {},
    deliverables: {}
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Pagination states
  const [page, setPage] = useState({
    campaigns: 0,
    partnerships: 0,
    deliverables: 0
  });
  const [rowsPerPage, setRowsPerPage] = useState({
    campaigns: 25,
    partnerships: 25,
    deliverables: 25
  });
  
  // Dialog states
  const [isCampaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [isPartnershipDialogOpen, setPartnershipDialogOpen] = useState(false);
  const [isDeliverableDialogOpen, setDeliverableDialogOpen] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, type: null });

  // Sorting functions
  const getStatusPriority = (type, status) => {
    const priorityMap = {
      campaigns: {
        'Active': 1,
        'Launched': 1,
        'Pending': 2,
        'Paused': 3,
        'Draft': 4,
        'Completed': 5,
        'Archived': 6
      },
      partnerships: {
        'active': 1,
        'pending': 2,
        'completed': 3
      },
      deliverables: {
        'accepted': 1,
        'sent': 2,
        'completed': 3,
        'cancelled': 4
      }
    };
    return priorityMap[type]?.[status] || 999;
  };

  const sortDataByDateAndStatus = (data, type) => {
    if (!data || !Array.isArray(data)) return [];
    
    return [...data].sort((a, b) => {
      // First, sort by status priority
      const statusA = type === 'campaigns' ? a.campaign_status : a.status;
      const statusB = type === 'campaigns' ? b.campaign_status : b.status;
      const priorityA = getStatusPriority(type, statusA);
      const priorityB = getStatusPriority(type, statusB);
      
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // Then sort by date (most recent first)
      const dateA = type === 'deliverables' ? a.created_at : a.proposal_date;
      const dateB = type === 'deliverables' ? b.created_at : b.proposal_date;
      
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      
      return new Date(dateB) - new Date(dateA);
    });
  };

  // Fetch all collaboration data
  const fetchCollaborations = useMutation(
    () => client.campaigns.getAdminCollaborations(),
    {
      onSuccess: (data) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('Fetched collaboration data:', data);
        }
        
        // Add defensive checks and provide defaults
        const campaigns = Array.isArray(data?.campaigns) ? data.campaigns : [];
        const partnerships = Array.isArray(data?.partnerships) ? data.partnerships : [];
        const deliverables = Array.isArray(data?.deliverables) ? data.deliverables : [];
        const summary = data?.summary || {
          total_campaigns: campaigns.length,
          total_partnerships: partnerships.length,
          total_deliverables: deliverables.length
        };
        
        // Sort data before setting state
        const sortedData = {
          campaigns: sortDataByDateAndStatus(campaigns, 'campaigns'),
          partnerships: sortDataByDateAndStatus(partnerships, 'partnerships'),
          deliverables: sortDataByDateAndStatus(deliverables, 'deliverables'),
          summary: summary
        };
        
        setCollaborationData(sortedData);
        
        // Initialize selection states
        const campaignSelection = sortedData.campaigns.reduce((acc, item) => {
          acc[item.id] = false;
          return acc;
        }, {});
        const partnershipSelection = sortedData.partnerships.reduce((acc, item) => {
          acc[item.id] = false;
          return acc;
        }, {});
        const deliverableSelection = sortedData.deliverables.reduce((acc, item) => {
          acc[item.id] = false;
          return acc;
        }, {});
        setSelectedItems({
          campaigns: campaignSelection,
          partnerships: partnershipSelection,
          deliverables: deliverableSelection
        });
        
        toast.success('Collaboration data loaded', {
          description: `${summary.total_campaigns} campaigns, ${summary.total_partnerships} partnerships, ${summary.total_deliverables} deliverables`
        });
      },
      onError: (error) => {
        console.error('Failed to fetch collaboration data:', error);
        toast.error('Failed to load data', {
          description: error?.response?.data?.error || error.message
        });
      }
    }
  );

  // Delete mutations
  const deleteCampaignMutation = useMutation(
    (campaignIds) => client.campaigns.deleteCampaignAdmin({ campaignIds }),
    {
      onSuccess: () => {
        toast.success('Campaigns deleted successfully');
        fetchCollaborations.mutate();
        setDeleteConfirmDialog({ open: false, type: null });
      },
      onError: (error) => {
        console.error('Error deleting campaigns:', error);
        toast.error('Failed to delete campaigns', {
          description: error?.response?.data?.error || error.message
        });
      }
    }
  );

  const deletePartnershipMutation = useMutation(
    (partnershipIds) => client.partnerships.delete({ partnershipIds }),
    {
      onSuccess: () => {
        toast.success('Partnerships deleted successfully');
        fetchCollaborations.mutate();
        setDeleteConfirmDialog({ open: false, type: null });
      },
      onError: (error) => {
        console.error('Error deleting partnerships:', error);
        toast.error('Failed to delete partnerships', {
          description: error?.response?.data?.error || error.message
        });
      }
    }
  );

  const deleteDeliverableMutation = useMutation(
    (deliverableIds) => client.deliverables.deleteDeliverablesAdmin({ deliverableIds }),
    {
      onSuccess: () => {
        toast.success('Deliverables deleted successfully');
        fetchCollaborations.mutate();
        setDeleteConfirmDialog({ open: false, type: null });
      },
      onError: (error) => {
        console.error('Error deleting deliverables:', error);
        toast.error('Failed to delete deliverables', {
          description: error?.response?.data?.error || error.message
        });
      }
    }
  );

  useEffect(() => {
    fetchCollaborations.mutate();
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSearchQuery('');
    setStatusFilter('');
  };

  const handleSelect = (type, id) => {
    setSelectedItems(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [id]: !prev[type][id]
      }
    }));
  };

  const handleSelectAll = (type, items, checked) => {
    const newSelection = items.reduce((acc, item) => {
      acc[item.id] = checked;
      return acc;
    }, {});
    setSelectedItems(prev => ({
      ...prev,
      [type]: newSelection
    }));
    toast.info(checked ? 'All items selected' : 'All items deselected', {
      description: `${checked ? items.length : 0} ${type} selected`
    });
  };

  const getSelectedCount = (type) => {
    return Object.values(selectedItems[type] || {}).filter(Boolean).length;
  };

  const getSelectedIds = (type) => {
    return Object.entries(selectedItems[type] || {})
      .filter(([_, selected]) => selected)
      .map(([id, _]) => parseInt(id));
  };

  const getSelectedItem = (type) => {
    const selectedIds = getSelectedIds(type);
    if (selectedIds.length !== 1) return null;
    return collaborationData[type]?.find(item => item.id === selectedIds[0]) || null;
  };

  const filterData = (data, type) => {
    if (!data) return [];
    let filtered = data;
    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(item => {
        if (type === 'campaigns') return item.campaign_status === statusFilter;
        if (type === 'partnerships') return item.status === statusFilter;
        if (type === 'deliverables') return item.status === statusFilter;
        return true;
      });
    }
    // Apply search query
    if (searchQuery) {
      filtered = filtered.filter(item => {
        const searchLower = searchQuery.toLowerCase();
        if (type === 'campaigns') {
          return item.name?.toLowerCase().includes(searchLower) ||
            item.brief?.toLowerCase().includes(searchLower) ||
            item.po_number?.toLowerCase().includes(searchLower);
        }
        if (type === 'partnerships') {
          return item.name?.toLowerCase().includes(searchLower) ||
            item.description?.toLowerCase().includes(searchLower) ||
            item.creator?.toLowerCase().includes(searchLower) ||
            item.email?.toLowerCase().includes(searchLower);
        }
        if (type === 'deliverables') {
          return item.deliverable_type?.toLowerCase().includes(searchLower) ||
            item.notes?.toLowerCase().includes(searchLower) ||
            item.creator_id?.toLowerCase().includes(searchLower) ||
            item.po_number?.toLowerCase().includes(searchLower);
        }
        return true;
      });
    }
    // Data is already sorted from fetchCollaborations, maintain that order
    return filtered;
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'Active': 'success',
      'Completed': 'default',
      'Pending': 'warning',
      'Launched': 'info',
      'Paused': 'warning',
      'Draft': 'default',
      'Archived': 'default',
      'pending': 'warning',
      'active': 'success',
      'sent': 'info',
      'accepted': 'success',
      'completed': 'default',
      'cancelled': 'error'
    };
    return statusColors[status] || 'default';
  };

  // Pagination handlers
  const handleChangePage = (type, newPage) => {
    setPage(prev => ({
      ...prev,
      [type]: newPage
    }));
  };

  const handleChangeRowsPerPage = (type, event) => {
    setRowsPerPage(prev => ({
      ...prev,
      [type]: parseInt(event.target.value, 10)
    }));
    setPage(prev => ({
      ...prev,
      [type]: 0
    }));
  };

  const getPaginatedData = (data, type) => {
    const startIndex = page[type] * rowsPerPage[type];
    const endIndex = startIndex + rowsPerPage[type];
    return data.slice(startIndex, endIndex);
  };

  // Action handlers
  const handleEdit = () => {
    const type = getCurrentTabData();
    if (getSelectedCount(type) !== 1) {
      toast.error('Please select exactly one item to edit');
      return;
    }
    if (type === 'campaigns') {
      setCampaignDialogOpen(true);
    } else if (type === 'partnerships') {
      setPartnershipDialogOpen(true);
    } else if (type === 'deliverables') {
      setDeliverableDialogOpen(true);
    }
  };

  const handleDelete = () => {
    const type = getCurrentTabData();
    const count = getSelectedCount(type);
    if (count === 0) {
      toast.error('Please select at least one item to delete');
      return;
    }
    setDeleteConfirmDialog({ open: true, type });
  };

  const confirmDelete = () => {
    const type = deleteConfirmDialog.type;
    const selectedIds = getSelectedIds(type);
    if (type === 'campaigns') {
      deleteCampaignMutation.mutate(selectedIds);
    } else if (type === 'partnerships') {
      deletePartnershipMutation.mutate(selectedIds);
    } else if (type === 'deliverables') {
      deleteDeliverableMutation.mutate(selectedIds);
    }
  };

  const handleDialogClose = (bRefresh) => {
    setCampaignDialogOpen(false);
    setPartnershipDialogOpen(false);
    setDeliverableDialogOpen(false);
    if (bRefresh) {
      fetchCollaborations.mutate();
    }
  };

  // Campaign Table
  const renderCampaignsTable = () => {
    const filteredCampaigns = filterData(collaborationData.campaigns, 'campaigns');
    const paginatedCampaigns = getPaginatedData(filteredCampaigns, 'campaigns');
    const allSelected = paginatedCampaigns.length > 0 &&
      paginatedCampaigns.every(c => selectedItems.campaigns[c.id]);
    const someSelected = paginatedCampaigns.some(c => selectedItems.campaigns[c.id]);
    
    return (
      <>
        <TableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onChange={(e) => handleSelectAll('campaigns', paginatedCampaigns, e.target.checked)}
                  />
                </TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Campaign Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Campaign Sum</TableCell>
                <TableCell>Videos</TableCell>
                <TableCell>Creators</TableCell>
                <TableCell>Proposal Date</TableCell>
                <TableCell>Product Type</TableCell>
                <TableCell>PO Number</TableCell>
                <TableCell>Blitz Auto</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedCampaigns.map((campaign) => (
                <TableRow
                  key={campaign.id}
                  selected={selectedItems.campaigns[campaign.id]}
                  hover
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedItems.campaigns[campaign.id] || false}
                      onChange={() => handleSelect('campaigns', campaign.id)}
                    />
                  </TableCell>
                  <TableCell>{campaign.id}</TableCell>
                  <TableCell>{campaign.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={campaign.campaign_status}
                      size="small"
                      color={getStatusColor(campaign.campaign_status)}
                    />
                  </TableCell>
                  <TableCell>{formatCurrency(campaign.campaign_sum)}</TableCell>
                  <TableCell>{campaign.video_amount}</TableCell>
                  <TableCell>
                    {campaign.creators?.length || 0}
                  </TableCell>
                  <TableCell>{formatDate(campaign.proposal_date)}</TableCell>
                  <TableCell>{campaign.campaign_type}</TableCell>
                  <TableCell>{campaign.po_number}</TableCell>
                  <TableCell>{campaign.blitz_autocampaign ? 'Yes' : 'No'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredCampaigns.length}
          page={page.campaigns}
          onPageChange={(e, newPage) => handleChangePage('campaigns', newPage)}
          rowsPerPage={rowsPerPage.campaigns}
          onRowsPerPageChange={(e) => handleChangeRowsPerPage('campaigns', e)}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </>
    );
  };

  // Partnership Table
  const renderPartnershipsTable = () => {
    const filteredPartnerships = filterData(collaborationData.partnerships, 'partnerships');
    const paginatedPartnerships = getPaginatedData(filteredPartnerships, 'partnerships');
    const allSelected = paginatedPartnerships.length > 0 &&
      paginatedPartnerships.every(p => selectedItems.partnerships[p.id]);
    const someSelected = paginatedPartnerships.some(p => selectedItems.partnerships[p.id]);
    
    return (
      <>
        <TableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onChange={(e) => handleSelectAll('partnerships', paginatedPartnerships, e.target.checked)}
                  />
                </TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Creator</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Workflow Stage</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Deliverables</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Proposal Date</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedPartnerships.map((partnership) => (
                <TableRow
                  key={partnership.id}
                  selected={selectedItems.partnerships[partnership.id]}
                  hover
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedItems.partnerships[partnership.id] || false}
                      onChange={() => handleSelect('partnerships', partnership.id)}
                    />
                  </TableCell>
                  <TableCell>{partnership.id}</TableCell>
                  <TableCell>{partnership.name}</TableCell>
                  <TableCell>{partnership.creator}</TableCell>
                  <TableCell>
                    <Chip
                      label={partnership.status}
                      size="small"
                      color={getStatusColor(partnership.status)}
                    />
                  </TableCell>
                  <TableCell>{partnership.workflow_stage}</TableCell>
                  <TableCell>{formatCurrency(partnership.computed_total)}</TableCell>
                  <TableCell>{partnership.deliverables_count}</TableCell>
                  <TableCell>{partnership.email}</TableCell>
                  <TableCell>{formatDate(partnership.proposal_date)}</TableCell>
                  <TableCell>
                    {partnership.notes ?
                      (partnership.notes.length > 50 ?
                        partnership.notes.substring(0, 50) + '...' :
                        partnership.notes)
                      : 'N/A'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredPartnerships.length}
          page={page.partnerships}
          onPageChange={(e, newPage) => handleChangePage('partnerships', newPage)}
          rowsPerPage={rowsPerPage.partnerships}
          onRowsPerPageChange={(e) => handleChangeRowsPerPage('partnerships', e)}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </>
    );
  };

  // Deliverable Table
  const renderDeliverablesTable = () => {
    const filteredDeliverables = filterData(collaborationData.deliverables, 'deliverables');
    const paginatedDeliverables = getPaginatedData(filteredDeliverables, 'deliverables');
    const allSelected = paginatedDeliverables.length > 0 &&
      paginatedDeliverables.every(d => selectedItems.deliverables[d.id]);
    const someSelected = paginatedDeliverables.some(d => selectedItems.deliverables[d.id]);
    
    return (
      <>
        <TableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onChange={(e) => handleSelectAll('deliverables', paginatedDeliverables, e.target.checked)}
                  />
                </TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Partnership</TableCell>
                <TableCell>Creator</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>PO Number</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedDeliverables.map((deliverable) => (
                <TableRow
                  key={deliverable.id}
                  selected={selectedItems.deliverables[deliverable.id]}
                  hover
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedItems.deliverables[deliverable.id] || false}
                      onChange={() => handleSelect('deliverables', deliverable.id)}
                    />
                  </TableCell>
                  <TableCell>{deliverable.id}</TableCell>
                  <TableCell>
                    <Chip
                      label={deliverable.deliverable_type}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {deliverable.partnership?.name || `ID: ${deliverable.partnership_id}`}
                  </TableCell>
                  <TableCell>{deliverable.creator_id}</TableCell>
                  <TableCell>{formatCurrency(deliverable.amount)}</TableCell>
                  <TableCell>
                    <Chip
                      label={deliverable.status}
                      size="small"
                      color={getStatusColor(deliverable.status)}
                    />
                  </TableCell>
                  <TableCell>{formatDate(deliverable.due_date)}</TableCell>
                  <TableCell>{deliverable.po_number}</TableCell>
                  <TableCell>{formatDate(deliverable.created_at)}</TableCell>
                  <TableCell>
                    {deliverable.notes ?
                      (deliverable.notes.length > 30 ?
                        deliverable.notes.substring(0, 30) + '...' :
                        deliverable.notes)
                      : 'N/A'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredDeliverables.length}
          page={page.deliverables}
          onPageChange={(e, newPage) => handleChangePage('deliverables', newPage)}
          rowsPerPage={rowsPerPage.deliverables}
          onRowsPerPageChange={(e) => handleChangeRowsPerPage('deliverables', e)}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </>
    );
  };

  const getCurrentTabData = () => {
    const types = ['campaigns', 'partnerships', 'deliverables'];
    return types[activeTab];
  };

  const getCurrentFilteredData = () => {
    const type = getCurrentTabData();
    return filterData(collaborationData[type] || [], type);
  };

  const getTabLabel = () => {
    const labels = {
      'campaigns': 'Campaign',
      'partnerships': 'Partnership',
      'deliverables': 'Deliverable'
    };
    return labels[getCurrentTabData()];
  };

  // Show loading backdrop
  if (fetchCollaborations.isLoading && !collaborationData.campaigns.length) {
    return (
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={true}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6">Loading collaboration data...</Typography>
        </Box>
      </Backdrop>
    );
  }

  return (
    <Box sx={{ padding: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Collaboration Administration</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh data">
            <IconButton
              onClick={() => fetchCollaborations.mutate()}
              disabled={fetchCollaborations.isLoading}
            >
              {fetchCollaborations.isLoading ? <CircularProgress size={24} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle2" color="textSecondary">Campaigns</Typography>
          <Typography variant="h5">{collaborationData.summary?.total_campaigns || 0}</Typography>
          <Typography variant="caption" color="textSecondary">
            {getSelectedCount('campaigns')} selected
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle2" color="textSecondary">Partnerships</Typography>
          <Typography variant="h5">{collaborationData.summary?.total_partnerships || 0}</Typography>
          <Typography variant="caption" color="textSecondary">
            {getSelectedCount('partnerships')} selected
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle2" color="textSecondary">Deliverables</Typography>
          <Typography variant="h5">{collaborationData.summary?.total_deliverables || 0}</Typography>
          <Typography variant="caption" color="textSecondary">
            {getSelectedCount('deliverables')} selected
          </Typography>
        </Paper>
      </Box>
      
      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Campaigns" />
          <Tab label="Partnerships" />
          <Tab label="Deliverables" />
        </Tabs>
      </Paper>
      
      {/* Action Buttons */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          disabled={getSelectedCount(getCurrentTabData()) !== 1}
          onClick={handleEdit}
          sx={StyleButton}
        >
          Edit {getTabLabel()}
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<DeleteIcon />}
          disabled={getSelectedCount(getCurrentTabData()) === 0}
          onClick={handleDelete}
          sx={StyleButton}
        >
          Delete Selected ({getSelectedCount(getCurrentTabData())})
        </Button>
      </Box>
      
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          label={`Search ${getCurrentTabData()}`}
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={`Search by name, ID, etc...`}
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
          }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {activeTab === 0 && (
              <>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Launched">Launched</MenuItem>
                <MenuItem value="Paused">Paused</MenuItem>
                <MenuItem value="Draft">Draft</MenuItem>
                <MenuItem value="Archived">Archived</MenuItem>
              </>
            )}
            {activeTab === 1 && (
              <>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </>
            )}
            {activeTab === 2 && (
              <>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="accepted">Accepted</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </>
            )}
          </Select>
        </FormControl>
      </Box>
      
      {/* Results count */}
      <Box sx={{ mb: 1 }}>
        <Typography variant="body2" color="textSecondary">
          Showing {getCurrentFilteredData().length} of {(collaborationData[getCurrentTabData()] || []).length} {getCurrentTabData()}
        </Typography>
      </Box>
      
      {/* Tables */}
      {activeTab === 0 && renderCampaignsTable()}
      {activeTab === 1 && renderPartnershipsTable()}
      {activeTab === 2 && renderDeliverablesTable()}
      
      {/* Campaign Dialog */}
      {isCampaignDialogOpen && (
        <CampaignDialog
          open={isCampaignDialogOpen}
          info={getSelectedItem('campaigns')}
          onClose={handleDialogClose}
        />
      )}
      
      {/* Partnership Dialog */}
      {isPartnershipDialogOpen && (
        <PartnershipDialog
          open={isPartnershipDialogOpen}
          info={getSelectedItem('partnerships')}
          onClose={handleDialogClose}
        />
      )}
      {/* Deliverable Dialog */}
      {isDeliverableDialogOpen && (
        <DeliverableDialog
          open={isDeliverableDialogOpen}
          info={getSelectedItem('deliverables')}
          onClose={handleDialogClose}
        />
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, type: null })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {getSelectedCount(deleteConfirmDialog.type)} {deleteConfirmDialog.type}?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialog({ open: false, type: null })}>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deleteCampaignMutation.isLoading ||
              deletePartnershipMutation.isLoading ||
              deleteDeliverableMutation.isLoading}
          >
            {(deleteCampaignMutation.isLoading ||
              deletePartnershipMutation.isLoading ||
              deleteDeliverableMutation.isLoading) ? (
              <CircularProgress size={24} />
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UnifiedCollaborationAdminView;